import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { AuthContext } from '../App';
import CrisisBanner from '../components/CrisisBanner';
import VentForm from '../components/VentForm';
import GraceCheckIn from '../components/GraceCheckIn';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';
import {
  remindersEnabled as remindersAlreadyEnabled,
  enableDailyReminders,
  initReminderSchedule,
} from '../lib/dailyReminder';

const todayKey = () => new Date().toISOString().split('T')[0];

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showCrisisBanner, setShowCrisisBanner] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);
  const [showGrace, setShowGrace] = useState(false);
  const [remindersOn, setRemindersOn] = useState(false);
  const [initialStyle, setInitialStyle] = useState('Dad');
  const [initialPlaceholder, setInitialPlaceholder] = useState('');
  const ventFormRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('activeConversation');
    if (stored) {
      try {
        setActiveConversation(JSON.parse(stored));
      } catch {
        localStorage.removeItem('activeConversation');
      }
    }

    // Grace Check-In visibility: show every visit until the user has vented today.
    const lastVent = localStorage.getItem('lastVentedAt');
    if (lastVent !== todayKey()) {
      setShowGrace(true);
    }

    setRemindersOn(remindersAlreadyEnabled());
    initReminderSchedule();
  }, []);

  const handleResult = (result) => {
    localStorage.setItem('lastVentedAt', todayKey());
    setShowGrace(false);
    navigate('/result', { state: { result, showCrisisBanner } });
  };

  const handleStartNewTopic = () => {
    localStorage.removeItem('activeConversation');
    setActiveConversation(null);
    toast.success('Started a new topic');
  };

  const handleMoodSelect = (mood) => {
    // Privacy-safe mood logging (fire-and-forget; admin only sees aggregate counts)
    api.post('/mood/log', { mood: mood.key }).catch(() => {});
    setInitialStyle(mood.persona);
    setInitialPlaceholder(mood.placeholder);
    setShowGrace(false);
    setTimeout(() => {
      ventFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      ventFormRef.current?.querySelector('textarea')?.focus();
    }, 100);
  };

  const handleDismissGrace = () => {
    setShowGrace(false);
  };

  const handleEnableReminders = async () => {
    const result = await enableDailyReminders();
    if (result.ok) {
      setRemindersOn(true);
      toast.success("Daily reminders on. We'll gently check in.");
    } else if (result.reason === 'denied') {
      toast.error('Notifications blocked. Enable them in your browser settings.');
    } else {
      toast.error("Your browser doesn't support notifications.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="home-page">
      <CrisisBanner show={showCrisisBanner} onDismiss={() => setShowCrisisBanner(false)} />
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mb-4" style={{ fontFamily: 'Outfit' }}>
            Share What's On <span className="text-[#8B5CF6]">Your Mind</span>
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">Express yourself freely. Choose how you'd like to receive support.</p>
          <p className="mt-4 text-sm sm:text-base text-[#D4AF37]/80 italic max-w-xl mx-auto" data-testid="home-tagline">
            You don't have to chase anything here. Sit down. Say what's real. Take your time.
          </p>
          {activeConversation && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-full bg-[#8B5CF6] bg-opacity-20 border border-[#8B5CF6]">
              <MessageCircle className="w-5 h-5 text-[#8B5CF6]" />
              <span className="text-sm text-[#F9FAFB]">Continuing: <span className="font-semibold">{activeConversation.title}...</span></span>
              <button onClick={handleStartNewTopic} className="ml-2 text-xs text-[#8B5CF6] hover:text-[#9D6FFF] underline" data-testid="start-new-topic-link">Start New Topic Instead</button>
            </div>
          )}
          {user && (
            <div className="mt-6 flex items-center justify-center gap-4">
              {!user.is_premium && (
                <div className="px-4 py-2 rounded-full bg-[#27272A] border border-[#8B5CF6] text-sm">
                  <span className="text-[#A1A1AA]">Free: </span>
                  <span className="text-[#F9FAFB] font-semibold">
                    {user.has_active_conversation
                      ? `${user.responses_in_active}/3 responses`
                      : user.conversations_today >= 1
                        ? 'Conversation used today'
                        : '1 free conversation today'}
                  </span>
                </div>
              )}
              {user.is_premium && (
                <div className="px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-sm font-bold">Premium Active - Unlimited</div>
              )}
            </div>
          )}
        </div>

        {showGrace && !activeConversation && (
          <GraceCheckIn
            onMoodSelect={handleMoodSelect}
            onDismiss={handleDismissGrace}
            onEnableReminders={handleEnableReminders}
            remindersEnabled={remindersOn}
          />
        )}

        <div ref={ventFormRef}>
          <VentForm
            activeConversation={activeConversation}
            onResult={handleResult}
            onCrisisDetected={setShowCrisisBanner}
            initialStyle={initialStyle}
            initialPlaceholder={initialPlaceholder}
          />
        </div>

        <div className="text-center text-sm text-[#A1A1AA]">
          <p>Need help? Visit our <a href="/support" className="text-[#8B5CF6] hover:text-[#9D6FFF]">Support Page</a> or review our <a href="/privacy" className="text-[#8B5CF6] hover:text-[#9D6FFF]">Privacy Policy</a></p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
